import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, Card, Typography, Space, Alert, Tag, Divider } from 'antd';
import { PhoneOutlined, LockOutlined, CrownOutlined, ShopOutlined, UserOutlined, SafetyOutlined } from '@ant-design/icons';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { LoginRequest } from '../types';

const { Title, Text } = Typography;

const LoginPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { isRTL } = useLocale();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [phoneNumber, setPhoneNumber] = useState('09120000000');
  const [password, setPassword] = useState('Admin@Parto360');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const quickRoles = [
    {
      title: '👑 مدیر کل (Admin)',
      phone: '09120000000',
      pass: 'Admin@Parto360',
      color: 'gold',
      role: 'admin'
    },
    {
      title: '🏢 آژانس همکار (Agency)',
      phone: '09129876543',
      pass: 'Agency@Parto360',
      color: 'blue',
      role: 'agency'
    },
    {
      title: '👤 کاربر مسافر (User)',
      phone: '09121234567',
      pass: 'User@Parto360',
      color: 'green',
      role: 'user'
    },
    {
      title: '⭐ حساب اختصاصی',
      phone: '09132655299',
      pass: 'Parto@360',
      color: 'purple',
      role: 'admin'
    }
  ];

  const handleQuickSelect = (phone: string, pass: string) => {
    setPhoneNumber(phone);
    setPassword(pass);
    setError('');
  };

  const handleLogin = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setError('لطفاً شماره موبایل معتبر (مثلاً 09120000000) را وارد کنید.');
      return;
    }
    if (!password) {
      setError('لطفاً رمز عبور را وارد کنید.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const cleanPhoneNumber = phoneNumber.startsWith('0') ? phoneNumber.substring(1) : phoneNumber;
      const data: LoginRequest = { 
        phoneNumber: cleanPhoneNumber, 
        password 
      };
      
      const result = await apiService.login(data);
      
      login(result.accessToken, {
        phoneNumber: cleanPhoneNumber,
        role: result.user?.role,
        firstName: result.user?.firstName || result.user?.full_name?.split(' ')[0] || 'کاربر',
        lastName: result.user?.lastName || result.user?.full_name?.split(' ').slice(1).join(' ') || '',
        id: result.user?.id,
      });
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Login Error:', err);
      setError(err.response?.data?.message || 'شماره موبایل یا رمز عبور اشتباه است.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)',
      padding: '20px',
      direction: 'rtl'
    }}>
      {/* Language Switcher */}
      <div style={{ 
        position: 'absolute',
        top: '20px',
        ...(isRTL ? { left: '20px', right: 'auto' } : { right: '20px', left: 'auto' }),
        zIndex: 1000
      }}>
        <LanguageSwitcher type="button" />
      </div>
      
      {/* Branding Logo & Title */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '24px',
        zIndex: 10
      }}>
        <div style={{
          width: '90px',
          height: '90px',
          borderRadius: '24px',
          background: 'rgba(255,255,255,0.18)',
          backdropFilter: 'blur(12px)',
          border: '2px solid rgba(255,255,255,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 12px 35px rgba(0,0,0,0.2)',
          margin: '0 auto 16px auto',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}>
          <img 
            src="/logo.png" 
            alt="پرتو ۳۶۰" 
            style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '14px',
              filter: 'brightness(0) invert(1)',
              objectFit: 'contain'
            }} 
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallbackDiv = document.createElement('div');
              fallbackDiv.style.cssText = `
                font-size: 30px;
                color: white;
                font-weight: 800;
                font-family: 'Vazirmatn', sans-serif;
              `;
              fallbackDiv.textContent = '۳۶۰';
              e.currentTarget.parentNode?.appendChild(fallbackDiv);
            }}
          />
        </div>
        <Title level={2} style={{ color: 'white', margin: 0, fontWeight: 800, letterSpacing: '-0.5px' }}>
          پرتو ۳۶۰
        </Title>
        <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>
          سامانه یکپارچه مدیریت سفر و گردشگری
        </Text>
      </div>

      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 420,
          borderRadius: '22px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
        bodyStyle={{ padding: '28px' }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>
              ورود به سامانه
            </Title>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              دسترسی امن به سطوح کاربری سامانه
            </Text>
          </div>

          <Form layout="vertical" onFinish={handleLogin}>
            <Form.Item 
              label={<span style={{ fontWeight: 600 }}>شماره موبایل</span>}
              style={{ marginBottom: '16px' }}
            >
              <Input
                size="large"
                prefix={<PhoneOutlined style={{ color: '#6366f1', marginLeft: '8px' }} />}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="مثال: 09120000000"
                style={{ 
                  borderRadius: '12px',
                  height: '50px',
                  direction: 'ltr',
                  textAlign: 'left',
                  fontSize: '15px'
                }}
              />
            </Form.Item>

            <Form.Item 
              label={<span style={{ fontWeight: 600 }}>رمز عبور</span>}
              style={{ marginBottom: '24px' }}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined style={{ color: '#6366f1', marginLeft: '8px' }} />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="رمز عبور خود را وارد کنید"
                style={{ 
                  borderRadius: '12px',
                  height: '50px',
                  direction: 'ltr',
                  textAlign: 'left',
                  fontSize: '15px'
                }}
              />
            </Form.Item>

            <Button 
              type="primary" 
              htmlType="submit"
              size="large"
              loading={loading}
              icon={<SafetyOutlined />}
              style={{ 
                width: '100%',
                height: '52px',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                border: 'none',
                boxShadow: '0 8px 20px rgba(79, 70, 229, 0.35)',
                transition: 'all 0.3s ease'
              }}
            >
              ورود به پرتو ۳۶۰
            </Button>
          </Form>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              style={{ borderRadius: '10px' }}
            />
          )}

          <Divider style={{ margin: '16px 0', fontSize: '12px', color: '#94a3b8' }}>
            انتخاب سریع جهت تست ۳ سطح کاربری
          </Divider>

          {/* Quick-fill helper chips for testing the 3 user levels */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {quickRoles.map((roleItem) => (
              <div
                key={roleItem.phone}
                onClick={() => handleQuickSelect(roleItem.phone, roleItem.pass)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderRadius: '10px',
                  background: phoneNumber === roleItem.phone ? '#f1f5f9' : '#f8fafc',
                  border: phoneNumber === roleItem.phone ? '1.5px solid #6366f1' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Tag color={roleItem.color} style={{ margin: 0, borderRadius: '6px' }}>
                    {roleItem.role.toUpperCase()}
                  </Tag>
                  <Text style={{ fontSize: '13px', fontWeight: 600 }}>{roleItem.title}</Text>
                </div>
                <Text type="secondary" style={{ fontSize: '12px', direction: 'ltr' }}>
                  {roleItem.phone}
                </Text>
              </div>
            ))}
          </div>

        </Space>
      </Card>
      
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
          © ۱۴۰۵ تمامی حقوق محفوظ است • سامانه پرتو ۳۶۰
        </Text>
      </div>
    </div>
  );
};

export default LoginPage;
